/**
 * Planner → Mine (Liam, 31 Oct): your own long-range plan. Projects, deadlines, Outlook meetings, travel and events on
 * one calendar (Month, Timeline, Year). Try mode lets you drag anything and see clashes without changing a thing; a
 * scenario saves the arrangement, Apply makes it real (trips, events and ONE deadlines; the rest is listed to change in
 * its own app). The crew board (Team) is untouched.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { AlertTriangle, CalendarPlus, Check, ChevronLeft, ChevronRight, ExternalLink, FlaskConical, Plane, PartyPopper, Rocket, Save, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, del, patch, post } from "../lib/api";
import { ErrorNote, Field, Modal } from "./ui";
import { ItemRow } from "./work";
import type { Item as WorkItem } from "../lib/work";
import { ListChecks } from "lucide-react";

type View = "month" | "timeline" | "year";
type Layer = "project" | "travel" | "event" | "launch" | "deadline" | "meeting";
type MoveKind = "entry" | "task" | "external" | "project";
interface EntryTask { id: string; ref: string; title: string; state: string; dueAt: string | null }
export type EntryKind = "travel" | "event" | "launch";
export interface Brief { type: string; audience: string[]; channels: string[]; extras: string[]; notes: string }
export interface Entry { id: string; kind: EntryKind; title: string; start: string; end: string; location: string | null; notes: string | null; tasks: EntryTask[]; projectKey?: string | null; brief?: Brief | null }
/** The "how are we launching?" choices; free text covers the rest. */
export const BRIEF = {
  type: ["Opening evening", "Open day", "Goes on the market", "Goes live online", "Soft opening", "Press launch"],
  audience: ["Members", "Clients", "Press", "Agents", "Buyers", "Staff", "Partners", "Public"],
  channels: ["Instagram", "Facebook", "Email", "WhatsApp", "Press release", "Signage", "Listing sites", "Website"],
  extras: ["Photos and video", "Catering", "Entertainment", "Giveaways", "Guided tours", "Offer or discount"],
};
const emptyBrief = (): Brief => ({ type: "", audience: [], channels: [], extras: [], notes: "" });
interface Data {
  from: string; to: string;
  entries: Entry[];
  projects: Array<{ id: string; source: string; ref: string | null; name: string; status: string; start: string; end: string; url: string | null; openEnded?: boolean }>;
  fusiontaskProjects: "connected" | "not_connected" | "error";
  deadlines: Array<{ id: string; source: string; ref: string | null; title: string; dueAt: string; state: string; url: string | null; movable: boolean }>;
  meetings: Array<{ subject: string; start: string; end: string; allDay: boolean; url: string }> | null;
}
interface Item { key: string; layer: Exclude<Layer, "meeting">; moveKind: MoveKind; id: string; ref: string | null; title: string; start: string; end: string; source: string; url: string | null; entry?: Entry; openEnded?: boolean }
interface Move { kind: MoveKind; id: string; start: string; end: string }
interface Scenario { id: string; name: string; moves: Move[]; createdAt: string }

const LAYERS: Array<{ key: Layer; label: string }> = [
  { key: "project", label: "Projects" }, { key: "deadline", label: "Deadlines" }, { key: "travel", label: "Travel" }, { key: "event", label: "Events" }, { key: "launch", label: "Launches" }, { key: "meeting", label: "Outlook" },
];
const TONE: Record<Item["layer"], string> = {
  project: "bg-violet-500/15 text-violet-800 dark:text-violet-200",
  travel: "bg-teal-500/20 text-teal-800 dark:text-teal-200",
  event: "bg-pink-500/15 text-pink-800 dark:text-pink-200",
  launch: "bg-amber-500/20 text-amber-900 dark:text-amber-200",
  deadline: "bg-red-500/15 text-red-700 dark:text-red-300",
};
const SOURCE: Record<string, string> = { one: "ONE", fusiontask: "FusionTask", "mythos-tasks": "Mythos Tasks", "mythos-ticketing": "Mythos Ticketing", "ai-procurement": "Procurement", fusionhire: "HR" };

// dates as YYYY-MM-DD, arithmetic in UTC
const ms = (d: string) => Date.parse(`${d}T00:00:00Z`);
const iso = (t: number) => new Date(t).toISOString().slice(0, 10);
const addDays = (d: string, n: number) => iso(ms(d) + n * 864e5);
const diff = (a: string, b: string) => Math.round((ms(b) - ms(a)) / 864e5);
const maltaDay = (instant: string) => new Date(instant).toLocaleDateString("en-CA", { timeZone: "Europe/Malta" });
const todayStr = () => maltaDay(new Date().toISOString());
const monthStart = (d: string) => `${d.slice(0, 7)}-01`;
const addMonths = (d: string, n: number) => { const x = new Date(ms(monthStart(d))); x.setUTCMonth(x.getUTCMonth() + n); return iso(x.getTime()); };
const mondayOf = (d: string) => addDays(d, -((new Date(ms(d)).getUTCDay() + 6) % 7));
const fmt = (d: string, o: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" }) => new Date(ms(d)).toLocaleDateString("en-GB", { ...o, timeZone: "UTC" });
const span = (a: string, b: string) => (a === b ? fmt(a) : `${fmt(a)} – ${fmt(b)}`);
const overlaps = (a: { start: string; end: string }, b: { start: string; end: string }) => a.start <= b.end && b.start <= a.end;

const store = { get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } }, set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } } };

function rangeFor(view: View, anchor: string, months: number) {
  if (view === "month") { const s = mondayOf(monthStart(anchor)); return { from: s, to: addDays(mondayOf(addDays(addMonths(anchor, 1), -1)), 6) }; }
  if (view === "year") return { from: `${anchor.slice(0, 4)}-01-01`, to: `${anchor.slice(0, 4)}-12-31` };
  // the current month starts at last week's Monday, so late in a month the view isn't mostly past
  const start = anchor === monthStart(todayStr()) ? addDays(mondayOf(todayStr()), -7) : monthStart(anchor);
  const late = anchor === monthStart(todayStr()) && Number(todayStr().slice(8)) >= 15; // …and runs a month further
  return { from: start < monthStart(anchor) ? monthStart(anchor) : start, to: addDays(addMonths(anchor, months + (late ? 1 : 0)), -1) };
}

/** Clashes on the arrangement as it stands (moves included). */
function findClashes(items: Item[]) {
  const out: string[] = [];
  const trips = items.filter((i) => i.layer === "travel");
  for (const t of trips) {
    for (const x of items) {
      if ((x.layer === "deadline" || x.layer === "event" || x.layer === "launch") && overlaps(t, x)) out.push(`${x.title} (${span(x.start, x.end)}) falls during ${t.title}`);
      if (x.layer === "project" && !x.openEnded && x.end >= t.start && x.end <= t.end) out.push(`${x.title} ends ${fmt(x.end)}, during ${t.title}`);
    }
  }
  const evs = items.filter((i) => i.layer === "event" || i.layer === "launch").sort((a, b) => a.start.localeCompare(b.start));
  for (let i = 0; i < evs.length; i++) for (let j = i + 1; j < evs.length; j++) {
    const gap = diff(evs[i]!.end, evs[j]!.start);
    if (gap > 3) break;
    out.push(gap <= 0 ? `${evs[i]!.title} and ${evs[j]!.title} overlap` : `${evs[i]!.title} and ${evs[j]!.title} are ${gap} day${gap === 1 ? "" : "s"} apart`);
  }
  const perWeek = new Map<string, number>();
  for (const d of items.filter((i) => i.layer === "deadline")) perWeek.set(mondayOf(d.end), (perWeek.get(mondayOf(d.end)) ?? 0) + 1);
  for (const [w, n] of [...perWeek].sort()) if (n >= 5) out.push(`Week of ${fmt(w)}: ${n} deadlines`);
  return out;
}

export function PlannerMine() {
  const qc = useQueryClient();
  const [view, setView] = useState<View>(() => (store.get("one.planner.mine.view") as View) || "timeline");
  const [months, setMonths] = useState(() => Number(store.get("one.planner.mine.months")) || 3);
  const [anchor, setAnchor] = useState(() => monthStart(todayStr()));
  const [layers, setLayers] = useState<Record<Layer, boolean>>(() => { try { return { project: true, travel: true, event: true, launch: true, deadline: true, meeting: false, ...JSON.parse(store.get("one.planner.mine.layers") ?? "{}") }; } catch { return { project: true, travel: true, event: true, launch: true, deadline: true, meeting: false }; } });
  const [tryMode, setTryMode] = useState(false);
  const [moves, setMoves] = useState<Record<string, Move>>({});
  const [editing, setEditing] = useState<Entry | { kind: EntryKind } | null>(null);
  const [result, setResult] = useState<{ applied: number; elsewhere: Array<Move & { title: string; url: string | null; source: string }>; failed: Array<{ id: string; error: string }> } | null>(null);
  useEffect(() => { store.set("one.planner.mine.view", view); store.set("one.planner.mine.months", String(months)); store.set("one.planner.mine.layers", JSON.stringify(layers)); }, [view, months, layers]);

  const { from, to } = rangeFor(view, anchor, months);
  const q = useQuery({ queryKey: ["planner-mine", from, to, layers.meeting], queryFn: () => api<Data>(`/api/planner/mine?from=${from}&to=${to}&meetings=${layers.meeting && view !== "year" ? 1 : 0}`), placeholderData: (prev) => prev });
  const scenarios = useQuery({ queryKey: ["planner-mine-scenarios"], queryFn: () => api<{ scenarios: Scenario[] }>("/api/planner/mine/scenarios") });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["planner-mine"] });

  const base = useMemo<Item[]>(() => {
    const d = q.data;
    if (!d) return [];
    return [
      ...d.projects.map((p) => ({ key: `project:${p.id}`, layer: "project" as const, moveKind: "project" as const, id: p.id, ref: p.ref, title: p.name, start: p.start, end: p.end, source: p.source, url: p.url, openEnded: p.openEnded })),
      ...d.entries.map((e) => ({ key: `entry:${e.id}`, layer: e.kind, moveKind: "entry" as const, id: e.id, ref: null, title: e.title, start: e.start, end: e.end, source: "one", url: null, entry: e })),
      ...d.deadlines.map((t) => { const day = maltaDay(t.dueAt); return { key: `${t.movable ? "task" : "external"}:${t.id}`, layer: "deadline" as const, moveKind: (t.movable ? "task" : "external") as MoveKind, id: t.id, ref: t.ref, title: t.title, start: day, end: day, source: t.source, url: t.url }; }),
    ];
  }, [q.data]);
  const items = useMemo(() => base.map((i) => (moves[i.key] ? { ...i, start: moves[i.key]!.start, end: moves[i.key]!.end } : i)), [base, moves]);
  const shown = items.filter((i) => layers[i.layer]);
  const clashes = useMemo(() => findClashes(items.filter((i) => layers[i.layer])), [items, layers]);
  const meetingsByDay = useMemo(() => {
    const m = new Map<string, Array<{ subject: string; start: string }>>();
    for (const e of q.data?.meetings ?? []) { const day = e.allDay ? e.start.slice(0, 10) : maltaDay(e.start); m.set(day, [...(m.get(day) ?? []), { subject: e.subject, start: e.start }]); }
    return m;
  }, [q.data]);

  const move = (it: Item, start: string, end: string) => {
    const orig = base.find((b) => b.key === it.key)!;
    setMoves((m) => { const n = { ...m }; if (orig.start === start && orig.end === end) delete n[it.key]; else n[it.key] = { kind: it.moveKind, id: it.id, start, end }; return n; });
  };
  const open = (it: Item) => { if (it.entry) setEditing(it.entry); else if (it.url) window.open(it.url, "_blank", "noopener"); };

  const apply = useMutation({
    mutationFn: () => post<{ applied: number; elsewhere: Move[]; failed: Array<{ id: string; error: string }> }>("/api/planner/mine/apply", { moves: Object.values(moves) }),
    onSuccess: (r) => {
      setResult({ ...r, elsewhere: r.elsewhere.map((m) => { const it = base.find((b) => b.id === m.id && b.moveKind === m.kind); return { ...m, title: it?.title ?? m.id, url: it?.url ?? null, source: it?.source ?? "" }; }) });
      setMoves({}); setTryMode(false); refresh();
    },
  });
  const saveScenario = useMutation({
    mutationFn: (name: string) => post("/api/planner/mine/scenarios", { name, moves: Object.values(moves) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["planner-mine-scenarios"] }),
  });
  const dropScenario = useMutation({ mutationFn: (id: string) => del(`/api/planner/mine/scenarios/${id}`), onSuccess: () => void qc.invalidateQueries({ queryKey: ["planner-mine-scenarios"] }) });
  const loadScenario = (s: Scenario) => {
    const n: Record<string, Move> = {};
    for (const m of s.moves) { const it = base.find((b) => b.id === m.id && b.moveKind === m.kind); if (it) n[it.key] = m; }
    setMoves(n); setTryMode(true);
  };
  const nMoves = Object.keys(moves).length;
  const step = (n: number) => setAnchor((a) => (view === "year" ? addMonths(a, 12 * n) : addMonths(a, view === "month" ? n : n)));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <button className="btn" aria-label="Back" onClick={() => step(-1)}><ChevronLeft size={16} /></button>
          <button className="btn" onClick={() => setAnchor(monthStart(todayStr()))}>Today</button>
          <button className="btn" aria-label="Forward" onClick={() => step(1)}><ChevronRight size={16} /></button>
        </div>
        <span className="text-sm font-medium">{view === "year" ? anchor.slice(0, 4) : view === "month" ? fmt(anchor, { month: "long", year: "numeric" }) : `${fmt(from, { month: "short", year: "numeric" })} – ${fmt(to, { month: "short", year: "numeric" })}`}</span>
        <div className="ml-auto flex flex-wrap items-center gap-1">
          {(["month", "timeline", "year"] as View[]).map((v) => <button key={v} className={clsx("btn btn-sm capitalize", view === v && "btn-primary")} onClick={() => setView(v)}>{v}</button>)}
          {view === "timeline" && <select className="input h-8 w-auto py-0 text-xs" value={months} onChange={(e) => setMonths(Number(e.target.value))} aria-label="Months shown"><option value={3}>3 months</option><option value={6}>6 months</option></select>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {LAYERS.map((l) => (
          <button key={l.key} className={clsx("rounded-full border px-2.5 py-0.5 text-xs", layers[l.key] ? "border-brand-400 bg-brand-500/10 text-brand-700 dark:text-brand-200" : "border-[var(--line)] text-[var(--muted)]")} onClick={() => setLayers((s) => ({ ...s, [l.key]: !s[l.key] }))} aria-pressed={layers[l.key]}>{l.label}</button>
        ))}
        <div className="ml-auto flex flex-wrap items-center gap-1">
          <button className="btn btn-sm" onClick={() => setEditing({ kind: "travel" })}><Plane size={13} /> Add trip</button>
          <button className="btn btn-sm" onClick={() => setEditing({ kind: "event" })}><PartyPopper size={13} /> Add event</button>
          <button className="btn btn-sm" onClick={() => setEditing({ kind: "launch" })} title="A service, product or property going live"><Rocket size={13} /> Add launch</button>
          <button className={clsx("btn btn-sm", tryMode && "btn-primary")} onClick={() => { if (tryMode && nMoves && !confirm("Leave Try mode and drop the changes you tried?")) return; if (tryMode) setMoves({}); setTryMode(!tryMode); }}><FlaskConical size={13} /> Try mode</button>
        </div>
      </div>

      {tryMode && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-400/50 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
          <span className="min-w-0 flex-1">Try mode: drag anything{view === "timeline" ? " (pull a bar's right edge to change its length)" : ""}. Nothing changes until you apply. {nMoves ? `${nMoves} change${nMoves === 1 ? "" : "s"} tried.` : ""}</span>
          <button className="btn btn-sm" disabled={!nMoves} onClick={() => setMoves({})}>Reset</button>
          <button className="btn btn-sm" disabled={!nMoves || saveScenario.isPending} onClick={() => { const n = prompt("Name this plan", `Plan ${String.fromCharCode(65 + (scenarios.data?.scenarios.length ?? 0) % 26)}`); if (n?.trim()) saveScenario.mutate(n.trim()); }}><Save size={13} /> Save plan</button>
          <button className="btn btn-sm btn-primary" disabled={!nMoves || apply.isPending} onClick={() => { if (confirm(`Apply ${nMoves} change${nMoves === 1 ? "" : "s"}? Trips, events and ONE deadlines move now; projects and other apps' tasks are listed for you to change there.`)) apply.mutate(); }}><Check size={13} /> Apply</button>
        </div>
      )}
      {!!scenarios.data?.scenarios.length && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="muted">Saved plans:</span>
          {scenarios.data.scenarios.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-1 rounded-full border border-[var(--line)] px-2 py-0.5">
              <button className="hover:underline" onClick={() => loadScenario(s)} title={`${s.moves.length} changes. Opens in Try mode.`}>{s.name}</button>
              <button aria-label={`Delete ${s.name}`} className="text-[var(--muted)] hover:text-red-600" onClick={() => { if (confirm(`Delete "${s.name}"?`)) dropScenario.mutate(s.id); }}><X size={11} /></button>
            </span>
          ))}
        </div>
      )}

      <ErrorNote error={q.error ?? apply.error ?? saveScenario.error} />
      {q.data && q.data.fusiontaskProjects !== "connected" && layers.project && (
        <p className="muted text-xs">FusionTask projects will show here once FusionTask shares them with ONE{q.data.fusiontaskProjects === "error" ? " (FusionTask didn't answer just now)" : ""}.</p>
      )}
      {q.data && layers.meeting && view !== "year" && q.data.meetings === null && <p className="muted text-xs">Connect Outlook in Settings to see your meetings here.</p>}

      {result && (
        <div className="rounded-lg border border-[var(--line)] p-3 text-sm">
          <div className="flex items-start gap-2">
            <Check size={16} className="mt-0.5 text-emerald-600" />
            <div className="min-w-0 flex-1">
              <p>{result.applied} change{result.applied === 1 ? "" : "s"} applied.{result.failed.length ? ` ${result.failed.length} couldn't be: ${result.failed.map((f) => f.error).join("; ")}.` : ""}</p>
              {!!result.elsewhere.length && (
                <>
                  <p className="muted mt-1 text-xs">Change these in their own app:</p>
                  <ul className="mt-1 space-y-0.5 text-xs">
                    {result.elsewhere.map((m) => (
                      <li key={m.id}>{SOURCE[m.source] ?? m.source}: {m.url ? <a className="text-brand-700 hover:underline" href={m.url} target="_blank" rel="noreferrer">{m.title}</a> : m.title} → {span(m.start, m.end)}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <button aria-label="Close" className="text-[var(--muted)]" onClick={() => setResult(null)}><X size={14} /></button>
          </div>
        </div>
      )}

      <div className="card overflow-x-auto p-3">
        {q.isLoading ? <p className="muted p-6 text-center text-sm">Loading your plan…</p>
          : view === "timeline" ? <Timeline from={from} to={to} items={shown} meetings={layers.meeting ? meetingsByDay : null} tryMode={tryMode} moved={moves} onMove={move} onOpen={open} />
          : view === "month" ? <MonthGrid from={from} to={to} month={anchor.slice(0, 7)} items={shown} meetings={layers.meeting ? meetingsByDay : null} tryMode={tryMode} moved={moves} onMove={move} onOpen={open} />
          : <YearStrip year={anchor.slice(0, 4)} items={shown} onPickMonth={(m) => { setAnchor(m); setView("month"); }} />}
      </div>

      <PlanTasks from={from} to={to} refreshKey={q.dataUpdatedAt} />

      <div className="text-sm">
        {clashes.length ? (
          <details>
            <summary className="flex cursor-pointer items-center gap-1.5 text-xs text-[var(--muted)] hover:text-[var(--fg)]"><AlertTriangle size={13} className="text-amber-600" /> {clashes.length} clash{clashes.length === 1 ? "" : "es"}{view === "year" ? " this year" : " in view"}</summary>
            <ul className="mt-1 divide-y divide-[var(--line)] text-xs">{clashes.slice(0, 30).map((c, i) => <li key={i} className="py-1 text-[var(--muted)]">{c}</li>)}</ul>
          </details>
        ) : q.data ? <p className="muted flex items-center gap-1.5 text-xs"><Check size={14} className="text-emerald-600" /> No clashes in view.</p> : null}
      </div>

      {editing && <EntryDialog entry={"id" in editing ? editing : null} kind={editing.kind} onClose={() => setEditing(null)} onSaved={refresh} />}
    </div>
  );
}

/** Rows: one per project, packed rows for trips and events, one for deadlines, one for meetings. */
function Timeline({ from, to, items, meetings, tryMode, moved, onMove, onOpen }: { from: string; to: string; items: Item[]; meetings: Map<string, Array<{ subject: string; start: string }>> | null; tryMode: boolean; moved: Record<string, Move>; onMove: (i: Item, s: string, e: string) => void; onOpen: (i: Item) => void }) {
  const days = diff(from, to) + 1;
  const pct = (d: string) => (Math.max(0, Math.min(days, diff(from, d))) / days) * 100;
  const pack = (list: Item[]) => {
    const rows: Item[][] = [];
    // a short bar's name sits beside it, so it takes up room after its end too
    const reach = (i: Item) => ((diff(i.start, i.end) + 1) / days < 0.07 ? addDays(i.end, Math.ceil(days * 0.16)) : i.end);
    for (const it of [...list].sort((a, b) => a.start.localeCompare(b.start))) {
      const row = rows.find((r) => reach(r[r.length - 1]!) < it.start);
      if (row) row.push(it); else rows.push([it]);
    }
    return rows;
  };
  const projects = items.filter((i) => i.layer === "project").sort((a, b) => a.start.localeCompare(b.start));
  const rows: Array<{ label: string; items: Item[] }> = [
    ...projects.map((p) => ({ label: p.title, items: [p] })),
    ...pack(items.filter((i) => i.layer === "travel")).map((r, n) => ({ label: n ? "" : "Travel", items: r })),
    ...pack(items.filter((i) => i.layer === "event")).map((r, n) => ({ label: n ? "" : "Events", items: r })),
    ...pack(items.filter((i) => i.layer === "launch")).map((r, n) => ({ label: n ? "" : "Launches", items: r })),
    ...(items.some((i) => i.layer === "deadline") ? [{ label: "Deadlines", items: items.filter((i) => i.layer === "deadline") }] : []),
  ];
  const monthTicks: string[] = [];
  for (let m = monthStart(from); m <= to; m = addMonths(m, 1)) monthTicks.push(m);
  const today = todayStr();
  const minW = days > 120 ? 1100 : 760;

  return (
    <div style={{ minWidth: minW }}>
      <div className="grid grid-cols-[180px_minmax(0,1fr)]">
        <div />
        <div className="relative h-6 border-b border-[var(--line)] text-[11px] text-[var(--muted)]">
          {monthTicks.map((m) => <span key={m} className="absolute top-0 border-l border-[var(--line)] pl-1" style={{ left: `${pct(m)}%` }}>{fmt(m, { month: "short" })}</span>)}
          {today >= from && today <= to && <span className="absolute top-3 -translate-x-1/2 text-[10px] font-medium text-brand-600" style={{ left: `${pct(today)}%` }}>today</span>}
        </div>
      </div>
      {!rows.length && !meetings && <p className="muted py-8 text-center text-sm">Nothing planned in these months. Add a trip or an event to start.</p>}
      {rows.map((r, n) => (
        <div key={n} className="grid grid-cols-[180px_minmax(0,1fr)] border-b border-[var(--line)]">
          <div className="truncate py-1.5 pr-2 text-xs text-[var(--muted)]" title={r.label}>{r.label}</div>
          <Lane from={from} days={days} pct={pct} items={r.items} today={today} tryMode={tryMode} moved={moved} onMove={onMove} onOpen={onOpen} />
        </div>
      ))}
      {meetings && (
        <div className="grid grid-cols-[180px_minmax(0,1fr)]">
          <div className="py-1.5 pr-2 text-xs text-[var(--muted)]">Outlook</div>
          <div className="relative h-8">
            {[...meetings].filter(([d]) => d >= from && d <= to).map(([d, list]) => (
              <span key={d} className="absolute top-2 h-4 rounded-sm bg-slate-500" style={{ left: `${pct(d)}%`, width: `max(3px, ${100 / days}%)`, opacity: Math.min(0.25 + list.length * 0.15, 0.9) }} title={`${fmt(d)}: ${list.length} meeting${list.length === 1 ? "" : "s"}\n${list.slice(0, 8).map((x) => x.subject).join("\n")}`} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Lane({ from, days, pct, items, today, tryMode, moved, onMove, onOpen }: { from: string; days: number; pct: (d: string) => number; items: Item[]; today: string; tryMode: boolean; moved: Record<string, Move>; onMove: (i: Item, s: string, e: string) => void; onOpen: (i: Item) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const drag = (e: React.PointerEvent, it: Item, mode: "move" | "end") => {
    if (!tryMode) return;
    e.preventDefault(); e.stopPropagation();
    const w = ref.current!.getBoundingClientRect().width, x0 = e.clientX, s0 = it.start, e0 = it.end;
    let moved = false;
    const mv = (m: PointerEvent) => {
      if (m.buttons === 0) { up(); return; } // the release happened outside the page
      const dd = Math.round(((m.clientX - x0) / w) * days);
      if (dd) moved = true;
      if (mode === "move") onMove(it, addDays(s0, dd), addDays(e0, dd));
      else onMove(it, s0, addDays(e0, Math.max(dd, -diff(s0, e0))));
    };
    const up = () => {
      window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", up);
      if (!moved && mode === "move") onOpen(it);
    };
    window.addEventListener("pointermove", mv); window.addEventListener("pointerup", up); window.addEventListener("pointercancel", up);
  };
  return (
    <div ref={ref} className="relative h-8">
      {today >= from && <span className="absolute inset-y-0 w-px bg-brand-400/60" style={{ left: `${pct(today)}%` }} />}
      {items.map((it) => {
        const tip = `${it.ref ? `${it.ref} ` : ""}${it.title}\n${it.openEnded ? `From ${fmt(it.start)}, no target date` : span(it.start, it.end)}${it.source !== "one" ? ` · ${SOURCE[it.source] ?? it.source}` : ""}${moved[it.key] ? "\n(moved in Try mode)" : ""}`;
        if (it.layer === "deadline") return (
          <button key={it.key} type="button" title={tip} onPointerDown={(e) => drag(e, it, "move")} onClick={() => !tryMode && onOpen(it)}
            className={clsx("absolute top-2 h-3.5 w-3.5 -translate-x-1/2 rotate-45 rounded-[2px]", it.source === "one" ? "bg-red-500" : "bg-red-400/70", moved[it.key] && "ring-2 ring-amber-500", tryMode && "cursor-grab")}
            style={{ left: `calc(${pct(it.end)}% + ${50 / days}%)` }} />
        );
        const left = pct(it.start), right = pct(addDays(it.end, 1));
        const short = (diff(it.start, it.end) + 1) / days < 0.07; // too narrow for its name: the name sits beside it
        return (
          <div key={it.key} className="contents">
          {short && <span className="pointer-events-none absolute top-1.5 whitespace-nowrap pl-1 text-[11px] leading-5 text-[var(--muted)]" style={{ left: `${right}%` }}>{it.title}</span>}
          <div title={tip} onPointerDown={(e) => drag(e, it, "move")} onClick={() => !tryMode && onOpen(it)}
            className={clsx("absolute top-1.5 flex h-5 items-center overflow-hidden whitespace-nowrap rounded px-1.5 text-[11px]", TONE[it.layer], moved[it.key] && "ring-2 ring-amber-500", tryMode ? "cursor-grab select-none outline-dashed outline-1 outline-[var(--line)]" : "cursor-pointer", it.openEnded && "rounded-r-none")}
            style={{ left: `${left}%`, width: `max(10px, ${right - left}%)`, ...(it.openEnded ? { maskImage: "linear-gradient(to right, black 85%, transparent)" } : {}) }}>
            {!short && <span className="truncate">{it.title}{it.openEnded ? " · no target date" : ""}</span>}
            {tryMode && !it.openEnded && <span onPointerDown={(e) => drag(e, it, "end")} className="absolute inset-y-0 right-0 w-2 cursor-ew-resize bg-black/10" aria-hidden />}
          </div>
          </div>
        );
      })}
    </div>
  );
}

/** Monday-first month grid; in Try mode drag a chip onto another day (multi-day items keep their length). */
function MonthGrid({ from, to, month, items, meetings, tryMode, moved, onMove, onOpen }: { from: string; to: string; month: string; items: Item[]; meetings: Map<string, Array<{ subject: string; start: string }>> | null; tryMode: boolean; moved: Record<string, Move>; onMove: (i: Item, s: string, e: string) => void; onOpen: (i: Item) => void }) {
  const days: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) days.push(d);
  const today = todayStr();
  const [over, setOver] = useState<string | null>(null);
  const drop = (day: string, key: string) => {
    const it = items.find((i) => i.key === key);
    if (!it) return;
    const len = diff(it.start, it.end);
    // a project dropped on a day moves its end there (that's the date that matters); everything else moves its start
    if (it.layer === "project") onMove(it, addDays(day, -len), day); else onMove(it, day, addDays(day, len));
  };
  const chipsOn = (day: string) => items.filter((i) => (i.layer === "project" ? i.start === day || (i.end === day && !i.openEnded) : i.start <= day && i.end >= day));
  return (
    <div className="min-w-[760px]">
      <div className="grid grid-cols-7 border-b border-[var(--line)] pb-1 text-center text-[11px] text-[var(--muted)]">{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d}>{d}</div>)}</div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const away = items.some((i) => i.layer === "travel" && i.start <= d && i.end >= d);
          const mt = meetings?.get(d);
          return (
            <div key={d} onDragOver={(e) => { if (tryMode) { e.preventDefault(); setOver(d); } }} onDragLeave={() => setOver(null)} onDrop={(e) => { setOver(null); drop(d, e.dataTransfer.getData("text/plain")); }}
              className={clsx("min-h-24 border-b border-r border-[var(--line)] p-1", d.slice(0, 7) !== month && "opacity-50", away && "bg-teal-500/[0.07]", over === d && "bg-amber-500/15")}>
              <div className={clsx("mb-0.5 flex items-center justify-between text-[11px]", d === today ? "font-semibold text-brand-600" : "text-[var(--muted)]")}>
                <span>{Number(d.slice(8))}</span>
                {mt && <span title={mt.slice(0, 10).map((x) => x.subject).join("\n")}>{mt.length} mtg</span>}
              </div>
              <div className="space-y-0.5">
                {chipsOn(d).slice(0, 5).map((it) => (
                  <div key={it.key} draggable={tryMode} onDragStart={(e) => e.dataTransfer.setData("text/plain", it.key)} onClick={() => onOpen(it)}
                    title={`${it.ref ? `${it.ref} ` : ""}${it.title}\n${span(it.start, it.end)}${it.source !== "one" ? ` · ${SOURCE[it.source] ?? it.source}` : ""}`}
                    className={clsx("truncate rounded px-1 text-[11px] leading-4", TONE[it.layer], moved[it.key] && "ring-1 ring-amber-500", tryMode ? "cursor-grab" : "cursor-pointer")}>
                    {it.layer === "project" ? (it.start === d ? "Starts: " : "Ends: ") : it.layer === "travel" && it.start !== d ? "· " : ""}{it.title}
                  </div>
                ))}
                {chipsOn(d).length > 5 && <div className="text-[10px] text-[var(--muted)]">+{chipsOn(d).length - 5} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The year at a glance: a strip per month, each day shaded by how much falls on it. */
function YearStrip({ year, items, onPickMonth }: { year: string; items: Item[]; onPickMonth: (m: string) => void }) {
  const load = (d: string) => items.reduce((n, i) => n + (i.start <= d && i.end >= d ? (i.layer === "project" ? 0.5 : i.layer === "deadline" ? 1 : 2) : 0), 0);
  const today = todayStr();
  return (
    <div className="min-w-[760px] space-y-1">
      {Array.from({ length: 12 }, (_, m) => {
        const ms0 = `${year}-${String(m + 1).padStart(2, "0")}-01`;
        const n = diff(ms0, addMonths(ms0, 1));
        return (
          <div key={m} className="grid grid-cols-[48px_minmax(0,1fr)] items-center gap-2">
            <button className="text-left text-xs text-[var(--muted)] hover:text-brand-600" onClick={() => onPickMonth(ms0)}>{fmt(ms0, { month: "short" })}</button>
            <div className="grid gap-px" style={{ gridTemplateColumns: `repeat(31, minmax(0, 1fr))` }}>
              {Array.from({ length: n }, (_, i) => {
                const d = addDays(ms0, i), l = load(d);
                const away = items.some((x) => x.layer === "travel" && x.start <= d && x.end >= d);
                const ev = items.find((x) => (x.layer === "event" || x.layer === "launch") && x.start <= d && x.end >= d);
                const names = items.filter((x) => x.layer !== "project" && x.start <= d && x.end >= d).map((x) => x.title);
                return (
                  <button key={d} onClick={() => onPickMonth(ms0)} title={`${fmt(d, { weekday: "short", day: "numeric", month: "short" })}${names.length ? `\n${names.slice(0, 8).join("\n")}` : ""}`}
                    className={clsx("h-6 rounded-sm", away ? "bg-teal-500/60" : ev ? "bg-pink-500/60" : l === 0 ? "bg-[var(--line)]/40" : "bg-brand-500", d === today && "ring-2 ring-brand-600")}
                    style={!away && !ev && l > 0 ? { opacity: Math.min(0.15 + l * 0.12, 0.9) } : undefined} />
                );
              })}
            </div>
          </div>
        );
      })}
      <p className="muted pt-2 text-[11px]">Teal: travel. Pink: events and launches. Blue: deadlines and projects, darker when busier. Click a month to open it.</p>
    </div>
  );
}

export function EntryDialog({ entry, kind, onClose, onSaved }: { entry: Entry | null; kind: EntryKind; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ kind: entry?.kind ?? kind, title: entry?.title ?? "", start: entry?.start ?? todayStr(), end: entry?.end ?? todayStr(), location: entry?.location ?? "", notes: entry?.notes ?? "" });
  const [projectKey, setProjectKey] = useState(entry?.projectKey ?? "");
  const [brief, setBrief] = useState<Brief>(entry?.brief ?? emptyBrief());
  const projects = useQuery({ queryKey: ["planner-projects"], queryFn: () => api<{ projects: Array<{ key: string; source: string; ref: string | null; name: string; due: string | null }> }>("/api/planner/mine/projects"), enabled: f.kind === "launch", staleTime: 60_000 });
  const flip = (k: "audience" | "channels" | "extras", v: string) => setBrief((b) => ({ ...b, [k]: b[k].includes(v) ? b[k].filter((x) => x !== v) : [...b[k], v] }));
  const [task, setTask] = useState({ title: "", due: entry ? addDays(entry.start, -7) < todayStr() ? todayStr() : addDays(entry.start, -7) : todayStr() });
  const [tasks, setTasks] = useState<EntryTask[]>(entry?.tasks ?? []);
  const briefSet = brief.type || brief.audience.length || brief.channels.length || brief.extras.length || brief.notes.trim();
  const body = { ...f, end: f.end < f.start ? f.start : f.end, location: f.location || null, notes: f.notes || null, projectKey: f.kind === "launch" && projectKey ? projectKey : null, brief: f.kind === "launch" && briefSet ? brief : null };
  const save = useMutation({ mutationFn: () => (entry ? patch(`/api/planner/mine/entries/${entry.id}`, body) : post("/api/planner/mine/entries", body)), onSuccess: () => { onSaved(); onClose(); } });
  const remove = useMutation({ mutationFn: () => del(`/api/planner/mine/entries/${entry!.id}`), onSuccess: () => { onSaved(); onClose(); } });
  const addTask = useMutation({
    mutationFn: () => post<{ item: { id: string; ref: string; title: string } }>(`/api/planner/mine/entries/${entry!.id}/tasks`, task),
    onSuccess: (r) => { setTasks((t) => [...t, { ...r.item, state: "open", dueAt: task.due }]); setTask((t) => ({ ...t, title: "" })); onSaved(); },
  });
  const travel = f.kind === "travel", launch = f.kind === "launch";
  const noun = travel ? "Trip" : launch ? "Launch" : "Event";
  return (
    <Modal title={entry ? `${noun}: ${entry.title}` : travel ? "Add a trip" : launch ? "Add a launch" : "Add an event"} onClose={onClose}
      footer={<>
        {entry && <button className="btn mr-auto text-red-600" disabled={remove.isPending} onClick={() => { if (confirm(`Delete "${entry.title}"? Its prep tasks stay.`)) remove.mutate(); }}><Trash2 size={14} /> Delete</button>}
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" disabled={!f.title.trim() || save.isPending} onClick={() => save.mutate()}>{entry ? "Save" : "Add"}</button>
      </>}>
      <div className="space-y-3">
        <div className="flex gap-1">
          {(["travel", "event", "launch"] as const).map((k) => <button key={k} className={clsx("btn btn-sm", f.kind === k && "btn-primary")} onClick={() => setF({ ...f, kind: k })}>{k === "travel" ? <><Plane size={13} /> Trip</> : k === "launch" ? <><Rocket size={13} /> Launch</> : <><PartyPopper size={13} /> Event</>}</button>)}
        </div>
        <Field label={noun} hint={launch ? "A service, product or property going live" : undefined}><input className="input" autoFocus value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={travel ? "Dubai, supplier visits" : launch ? "Fortress Gardens apartment goes on the market" : "Mythos Christmas party"} /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label={travel ? "Leave" : "From"}><input type="date" className="input" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value, end: f.end < e.target.value ? e.target.value : f.end })} /></Field>
          <Field label={travel ? "Back" : "To"}><input type="date" className="input" value={f.end} min={f.start} onChange={(e) => setF({ ...f, end: e.target.value })} /></Field>
        </div>
        <Field label={travel ? "Where" : launch ? "Where (optional)" : "Venue"}><input className="input" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} /></Field>
        <Field label="Notes"><textarea className="input min-h-20" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
        {launch && (
          <div className="space-y-3 border-t border-[var(--line)] pt-3">
            <Field label="Project it belongs to" hint="The build this launch presents. Prepare shows the two together.">
              <select className="input" value={projectKey} onChange={(e) => setProjectKey(e.target.value)}>
                <option value="">No project</option>
                {projects.data?.projects.map((pr) => <option key={pr.key} value={pr.key}>{pr.name}{pr.ref ? ` (${pr.ref})` : ""}{pr.source === "fusiontask" ? " · FusionTask" : ""}{pr.due ? ` · ${fmt(pr.due)}` : ""}</option>)}
              </select>
            </Field>
            <p className="text-sm font-medium">How are we launching?</p>
            <Field label="Kind of launch">
              <input className="input" list="brief-type" value={brief.type} onChange={(e) => setBrief({ ...brief, type: e.target.value })} placeholder="Opening evening" />
              <datalist id="brief-type">{BRIEF.type.map((t) => <option key={t} value={t} />)}</datalist>
            </Field>
            {(["audience", "channels", "extras"] as const).map((k) => (
              <Field key={k} label={k === "audience" ? "For whom" : k === "channels" ? "How we tell people" : "Extras"}>
                <div className="flex flex-wrap gap-1">
                  {BRIEF[k].map((v) => <button key={v} type="button" className={clsx("rounded-full border px-2.5 py-0.5 text-xs", brief[k].includes(v) ? "border-brand-400 bg-brand-500/10 text-brand-700 dark:text-brand-200" : "border-[var(--line)] text-[var(--muted)]")} onClick={() => flip(k, v)} aria-pressed={brief[k].includes(v)}>{v}</button>)}
                </div>
              </Field>
            ))}
            <Field label="Anything else about the launch"><textarea className="input min-h-16" value={brief.notes} onChange={(e) => setBrief({ ...brief, notes: e.target.value })} placeholder="Ribbon cutting with the mayor at 7pm; 80 guests; the bar area isn't ready so keep guests to the spa floor" /></Field>
            <p className="muted text-xs">Save, then in Prepare press "Draft the launch plan": Fusion AI writes the checklist for this launch from these answers.</p>
          </div>
        )}
        {entry ? (
          <div className="border-t border-[var(--line)] pt-3">
            <p className="mb-1.5 flex items-center gap-1.5 text-sm font-medium"><CalendarPlus size={14} /> Prep tasks</p>
            {tasks.length ? (
              <ul className="mb-2 space-y-0.5 text-xs">
                {tasks.map((t) => <li key={t.id} className={clsx(t.state === "done" && "line-through opacity-60")}><a className="text-brand-700 hover:underline" href={`/work/${t.id}`} target="_blank" rel="noreferrer">{t.ref} {t.title}</a>{t.dueAt ? ` · due ${fmt(maltaDay(t.dueAt))}` : ""} <ExternalLink size={10} className="inline" /></li>)}
              </ul>
            ) : <p className="muted mb-2 text-xs">Things to get done beforehand become tasks in your list, due on the day you pick.</p>}
            <div className="flex gap-1.5">
              <input className="input flex-1" value={task.title} onChange={(e) => setTask({ ...task, title: e.target.value })} placeholder={travel ? "Book flights" : launch ? "Photos and listing" : "Book the venue"} onKeyDown={(e) => { if (e.key === "Enter" && task.title.trim()) addTask.mutate(); }} />
              <input type="date" className="input w-36" value={task.due} onChange={(e) => setTask({ ...task, due: e.target.value })} aria-label="Due" />
              <button className="btn" disabled={!task.title.trim() || addTask.isPending} onClick={() => addTask.mutate()}>Add</button>
            </div>
            <ErrorNote error={addTask.error} />
          </div>
        ) : <p className="muted text-xs">After adding it, open it again to list prep tasks{travel ? "" : ", or get it ready in Planner → Prepare"}.</p>}
        <ErrorNote error={save.error ?? remove.error} />
      </div>
    </Modal>
  );
}

/** Under the calendar: the main task of each event or launch in view, its sub-tasks behind a + (Liam, 31 Oct). */
function PlanTasks({ from, to, refreshKey }: { from: string; to: string; refreshKey: number }) {
  const q = useQuery({ queryKey: ["planner-mine-tasks", from, to, refreshKey], queryFn: () => api<{ tasks: Array<{ entryId: string; kind: string; title: string; start: string; task: (WorkItem & { subtasks: { total: number; done: number } | null }) | null }> }>(`/api/planner/mine/tasks?from=${from}&to=${to}`), placeholderData: (prev) => prev });
  const rows = q.data?.tasks ?? [];
  if (!q.data) return null;
  return (
    <div className="card p-0">
      <p className="flex items-center gap-1.5 px-4 pt-3 text-sm font-medium"><ListChecks size={15} /> Getting ready</p>
      {!rows.length ? <p className="muted px-4 pb-3 pt-1 text-xs">No events or launches in view. Add one above, then make its task in Prepare.</p> : (
        <ul className="px-2 pb-2">
          {rows.map((r) => r.task ? <MainTask key={r.entryId} i={r.task} /> : (
            <li key={r.entryId} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="muted min-w-0 flex-1 truncate">{r.kind === "launch" ? "Launch" : "Event"}: {r.title} <span className="text-xs">· {fmt(r.start)}</span></span>
              <span className="muted text-xs">no task yet: make it in Prepare</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MainTask({ i }: { i: WorkItem & { subtasks: { total: number; done: number } | null } }) {
  const [open, setOpen] = useState(false);
  const kids = useQuery({ queryKey: ["items", "children", i.id], queryFn: () => api<{ items: WorkItem[] }>(`/api/work/items?parentId=${i.id}&state=open,in_progress,waiting,done,cancelled&limit=100`), enabled: open });
  const rows = [...(kids.data?.items ?? [])].sort((a, b) => Number(["done", "cancelled"].includes(a.state)) - Number(["done", "cancelled"].includes(b.state)));
  return (
    <>
      <ItemRow i={i} subOpen={open} onSubToggle={i.subtasks ? () => setOpen((x) => !x) : undefined} />
      {open && (
        <li className="ml-6 border-l-2 border-[var(--line)] pl-2">
          {kids.isLoading ? <p className="muted px-3 py-2 text-xs">Loading sub-tasks…</p> : <ul>{rows.map((c) => <ItemRow key={c.id} i={c} />)}</ul>}
        </li>
      )}
    </>
  );
}
